import cn from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { last } from 'lodash';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AIRoleEnum, Message } from 'types/ai.types';
import { useAIChatContext } from './AIChat.context';
import AIChatDataShareModal from './AIChatDataShareModal';
import AIChatMessageGratification from './AIChatMessageGratification';
import AIChatMessageItem from './AIChatMessageItem';
import AIChatSpinner from './AIChatMessageSkeleton';
import AIChatRating from './AIChatRating';
import AIChatSource from './AIChatSource';

type AIChatProps = {
  isSmall?: boolean;
  initialMessage?: string;
  className?: string;
  lastMessageActions?: React.ReactNode;
  renderMessage?: (message: Message) => JSX.Element;
  showSource?: boolean;
  fullWidthMessages?: boolean;
  hideRating?: boolean;
};

const AIChat: FC<AIChatProps> = ({
  isSmall = false,
  initialMessage,
  className,
  lastMessageActions,
  renderMessage,
  showSource,
  fullWidthMessages = true,
  hideRating = false,
}) => {
  const { streamedData, conversation, currentTipId, isLoading, usage } =
    useAIChatContext();
  const { t } = useTranslation();

  const lastMessage = useMemo(
    () => last(conversation?.messages),
    [conversation?.messages],
  );

  const allMessages = streamedData?.length
    ? conversation?.messages?.concat([
        {
          _id: 'stream',
          role: AIRoleEnum.assistant,
          content: streamedData,
        },
      ])
    : conversation?.messages;

  return (
    <div className={cn('flex flex-col', className)}>
      {isSmall && (
        <AIChatDataShareModal
          showIcon
          title={t('The context is sent to OpenAI for processing')}
        />
      )}

      {showSource && conversation?._id ? <AIChatSource /> : null}

      {!conversation && currentTipId ? (
        <AIChatMessageGratification gratificationId={currentTipId} />
      ) : null}

      {initialMessage && (
        <AIChatMessageItem
          isSmall={isSmall}
          isFullWidth={fullWidthMessages}
          message={{
            _id: 'initial',
            role: AIRoleEnum.assistant,
            content: initialMessage,
          }}
        />
      )}

      {allMessages?.map((message) => (
        <AIChatMessageItem
          key={message.content}
          isSmall={isSmall}
          isFullWidth={fullWidthMessages}
          renderMessage={renderMessage}
          message={message}
          isLastAssistantMessage={
            lastMessage?._id === message._id && lastMessage.role !== 'user'
          }
        />
      ))}
      {isLoading && !streamedData && (
        <div className="p-4 mt-4">
          <AIChatSpinner size={isSmall ? 'small' : 'medium'} />
        </div>
      )}
      {!isLoading &&
        lastMessage &&
        lastMessage.role !== 'user' &&
        lastMessage._id !== 'stream' &&
        lastMessageActions}

      {usage?.reachedMaxCount && conversation?.status === 'processed' ? (
        <Alert
          type="warning"
          description={t('ai_bot.message_count_warning', {
            limit: usage?.conversation_message_limit,
          })}
          className="mt-3"
        />
      ) : null}

      {hideRating ? null : <AIChatRating isSmall={isSmall} />}
    </div>
  );
};

export default AIChat;
